import type { AISummary, AISummaryResponse } from '@fig/shared';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import apiClient from '../../lib/api-client';

export const summaryKeys = {
  all: ['ai-summaries'] as const,
  detail: (opportunityId: string) => [...summaryKeys.all, opportunityId] as const,
  detailWithProducts: (opportunityId: string, productIds?: string[]) =>
    [...summaryKeys.detail(opportunityId), productIds] as const,
};

async function fetchSummary(
  opportunityId: string,
  productIds?: string[]
): Promise<AISummary | null> {
  const params = productIds?.length ? `?productIds=${productIds.join(',')}` : '';
  const response = await apiClient.get<AISummaryResponse>(
    `/comparisons/${opportunityId}/summary${params}`
  );
  return response.data.summary ?? null;
}

async function regenerateSummary(
  opportunityId: string,
  productIds?: string[]
): Promise<AISummary | null> {
  const productParam = productIds?.length ? `&productIds=${productIds.join(',')}` : '';
  const response = await apiClient.get<AISummaryResponse>(
    `/comparisons/${opportunityId}/summary?regenerate=true${productParam}`
  );
  return response.data.summary ?? null;
}

export function useAISummary(opportunityId: string, enabled = true, productIds?: string[]) {
  return useQuery({
    queryKey: summaryKeys.detailWithProducts(opportunityId, productIds),
    queryFn: () => fetchSummary(opportunityId, productIds),
    enabled: !!opportunityId && enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    retry: 1,
  });
}

export function useRegenerateAISummary(opportunityId: string, productIds?: string[]) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => regenerateSummary(opportunityId, productIds),
    onSuccess: (data) => {
      queryClient.setQueryData(summaryKeys.detailWithProducts(opportunityId, productIds), data);
    },
  });
}
